import { getIntegrationDisplayName, IntegrationServiceId } from '../../../../common/integrations'
import React from 'react'
import { PhoneHowItWorks } from './connectors/PhoneHowItWorks'
import { ZoomHowItWorks } from './connectors/ZoomHowItWorks'
import { TeamsHowItWorks } from './connectors/TeamsHowItWorks'
import { MeetHowItWorks } from './connectors/MeetHowItWorks'
import { WebexHowItWorks } from './connectors/WebexHowItWorks'
import { GoogleCalendarHowItWorks } from './connectors/GoogleCalendarHowItWorks'
import { OutlookCalendarHowItWorks } from './connectors/OutlookCalendarHowItWorks'
import { ExchangeCalendarHowItWorks } from './connectors/ExchangeCalendarHowItWorks'
import { GmailHowItWorks } from './connectors/GmailHowItWorks'
import { OutlookMailHowItWorks } from './connectors/OutlookMailHowItWorks'
import { eMoneyHowItWorks } from './connectors/eMoneyHowItWorks'
import { RightCapitalHowItWorks } from './connectors/RightCapitalHowItWorks'
import { OrionHowItWorks } from './connectors/OrionHowItWorks'
import { HolistiplanHowItWorks } from './connectors/HolistiplanHowItWorks'
import { WealthHowItWorks } from './connectors/WealthHowItWorks'
import { WoveHowItWorks } from './connectors/WoveHowItWorks'
import { BlackDiamondWPHowItWorks } from './connectors/BlackDiamondWPHowItWorks'
import { RingCentralHowItWorks } from './connectors/RingCentralHowItWorks'
import { ConquestPlanningHowItWorks } from './connectors/ConquestPlanningHowItWorks'
import { SchwabHowItWorks } from './connectors/SchwabHowItWorks'

type HowItWorksProps = { displayName?: string }
type TemplateId = 'CRM_BASE' | 'CRM_WITH_WORKFLOWS' | 'CRM_MINI_4' | 'PRECISE_FP'

const TEMPLATES: Record<TemplateId, readonly string[]> = {
  CRM_BASE: [
    'Sync contacts between {NAME} and Zocks',
    'Create new or update your {NAME} contacts from meeting insights, emails and documents',
    'Save Zocks Meeting Summaries to {NAME}',
    'Create and assign Tasks in {NAME}',
    'Prepare for client meetings using combined data from Zocks, {NAME}, and your connected systems.',
    'Get instant insights from {NAME} with Zocks’ Ask Anything AI Assistant',
    'Generate email replies based on {NAME} information',
    'Fill Zocks Forms utilizing {NAME} data',
  ],
  CRM_WITH_WORKFLOWS: [
    'Sync contacts between {NAME} and Zocks',
    'Create new or update your {NAME} contacts from meeting insights, emails and documents',
    'Save Zocks Meeting Summaries to {NAME}',
    'Create and assign Tasks in {NAME}',
    'Trigger or update Workflows in {NAME}',
    'Prepare for client meetings using combined data from Zocks, {NAME}, and your connected systems.',
    'Get instant insights from {NAME} with Zocks’ Ask Anything AI Assistant',
    'Generate email replies based on {NAME} information',
    'Fill Zocks Forms utilizing {NAME} data',
  ],
  CRM_MINI_4: [
    'Sync contacts between {NAME} and Zocks',
    'Create new or update your {NAME} contacts from meeting insights, emails and documents',
    'Save Zocks Meeting Summaries to {NAME}',
    'Create and assign Tasks in {NAME}',
  ],
  PRECISE_FP: [
    'Sync contacts between {NAME} and Zocks',
    'Create new or update your {NAME} contacts from meeting insights, emails and documents',
    'Prepare for client meetings using combined data from Zocks, {NAME}, and your connected systems.',
    'Get instant insights from {NAME} with Zocks’ Ask Anything AI Assistant',
    'Generate email replies based on {NAME} information',
    'Fill Zocks Forms utilizing {NAME} data',
  ],
}

const HowItWorksList: React.FC<{ lines: readonly string[] }> = ({ lines }) => (
  <div>
    <ul>
      {lines.map((line, i) => (
        <li key={i}>{line}</li>
      ))}
    </ul>
  </div>
)

const makeHowItWorks = (
  serviceId: IntegrationServiceId,
  templateId: TemplateId,
  options?: {
    displayName?: string
  },
): React.FC<HowItWorksProps> => {
  const fallbackName = options?.displayName ?? getIntegrationDisplayName(serviceId) ?? String(serviceId)

  const C: React.FC<HowItWorksProps> = ({ displayName }) => {
    const name = displayName ?? fallbackName
    const lines = TEMPLATES[templateId].map((l) => l.replaceAll('{NAME}', name))
    return <HowItWorksList lines={lines} />
  }

  C.displayName = `${fallbackName}HowItWorks`
  return C
}

const ElementsHowItWorks = makeHowItWorks(IntegrationServiceId.ELEMENTS, 'CRM_BASE', { displayName: 'Elements' })
const EngageHowItWorks = makeHowItWorks(IntegrationServiceId.ENGAGE, 'CRM_BASE', { displayName: 'Engage' })
const BlackDiamondHowItWorks = makeHowItWorks(IntegrationServiceId.BLACK_DIAMOND, 'CRM_BASE', { displayName: 'Edge' })
const HubSpotHowItWorks = makeHowItWorks(IntegrationServiceId.HUBSPOT, 'CRM_BASE')
const SalesforceHowItWorks = makeHowItWorks(IntegrationServiceId.SALESFORCE, 'CRM_BASE')
const PractifiHowItWorks = makeHowItWorks(IntegrationServiceId.PRACTIFI, 'CRM_BASE')
const XLR8HowItWorks = makeHowItWorks(IntegrationServiceId.XLR8, 'CRM_BASE')
const ZohoHowItWorks = makeHowItWorks(IntegrationServiceId.ZOHO, 'CRM_BASE')
const DynamicsHowItWorks = makeHowItWorks(IntegrationServiceId.DYNAMICS, 'CRM_BASE')
const SmartOfficeHowItWorks = makeHowItWorks(IntegrationServiceId.SMARTOFFICE, 'CRM_BASE')

const WealthboxHowItWorks = makeHowItWorks(IntegrationServiceId.WEALTHBOX, 'CRM_WITH_WORKFLOWS')
const RedtailHowItWorks = makeHowItWorks(IntegrationServiceId.REDTAIL, 'CRM_WITH_WORKFLOWS')

const AdvisorEngineHowItWorks = makeHowItWorks(IntegrationServiceId.ADVISOR_ENGINE, 'CRM_MINI_4')
const PreciseFPHowItWorks = makeHowItWorks(IntegrationServiceId.PRECISE_FP, 'PRECISE_FP')

const INTEGRATION_HOW_IT_WORKS: Partial<Record<IntegrationServiceId, React.FC>> = {
  [IntegrationServiceId.PHONE]: PhoneHowItWorks,
  [IntegrationServiceId.ZOOM]: ZoomHowItWorks,
  [IntegrationServiceId.TEAMS]: TeamsHowItWorks,
  [IntegrationServiceId.MEET]: MeetHowItWorks,
  [IntegrationServiceId.WEBEX]: WebexHowItWorks,
  [IntegrationServiceId.GOOGLE_CALENDAR]: GoogleCalendarHowItWorks,
  [IntegrationServiceId.OUTLOOK_CALENDAR]: OutlookCalendarHowItWorks,
  [IntegrationServiceId.EXCHANGE_CALENDAR]: ExchangeCalendarHowItWorks,
  [IntegrationServiceId.GMAIL]: GmailHowItWorks,
  [IntegrationServiceId.OUTLOOK_MAIL]: OutlookMailHowItWorks,
  [IntegrationServiceId.E_MONEY]: eMoneyHowItWorks,
  [IntegrationServiceId.RIGHT_CAPITAL]: RightCapitalHowItWorks,
  [IntegrationServiceId.ORION]: OrionHowItWorks,
  [IntegrationServiceId.HOLISTIPLAN]: HolistiplanHowItWorks,
  [IntegrationServiceId.ELEMENTS]: ElementsHowItWorks,
  [IntegrationServiceId.BLACK_DIAMOND]: BlackDiamondHowItWorks,
  [IntegrationServiceId.ENGAGE]: EngageHowItWorks,
  [IntegrationServiceId.HUBSPOT]: HubSpotHowItWorks,
  [IntegrationServiceId.SALESFORCE]: SalesforceHowItWorks,
  [IntegrationServiceId.PRACTIFI]: PractifiHowItWorks,
  [IntegrationServiceId.XLR8]: XLR8HowItWorks,
  [IntegrationServiceId.ZOHO]: ZohoHowItWorks,
  [IntegrationServiceId.DYNAMICS]: DynamicsHowItWorks,
  [IntegrationServiceId.SMARTOFFICE]: SmartOfficeHowItWorks,
  [IntegrationServiceId.WEALTHBOX]: WealthboxHowItWorks,
  [IntegrationServiceId.REDTAIL]: RedtailHowItWorks,
  [IntegrationServiceId.ADVISOR_ENGINE]: AdvisorEngineHowItWorks,
  [IntegrationServiceId.PRECISE_FP]: PreciseFPHowItWorks,
  [IntegrationServiceId.WEALTH]: WealthHowItWorks,
  [IntegrationServiceId.WOVE]: WoveHowItWorks,
  [IntegrationServiceId.BLACK_DIAMOND_WP]: BlackDiamondWPHowItWorks,
  [IntegrationServiceId.RINGCENTRAL]: RingCentralHowItWorks,
  [IntegrationServiceId.CONQUEST_PLANNING]: ConquestPlanningHowItWorks,
  [IntegrationServiceId.SCHWAB]: SchwabHowItWorks,
}

export const HowItWorksComponent: React.FC<{ serviceId: IntegrationServiceId }> = ({ serviceId }) => {
  const Component = INTEGRATION_HOW_IT_WORKS[serviceId]
  return Component ? <Component /> : <div>No additional information available for this integration.</div>
}
